import { Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxCopytextModule } from '@aposin/ng-aquila/copytext';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxLinkModule } from '@aposin/ng-aquila/link';
import { NxListModule } from '@aposin/ng-aquila/list';
import { NxDialogService } from '@aposin/ng-aquila/modal';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

import { displayError } from '#components/modals/error-modal';
import { ControllerCode } from '#models/MicroController.model';
import CveService from '#services/cve/cve.service';
import UserService from '#services/user/user.service';
import { GERL_SHARE_POINT_LINK, REP_RISK_SHARE_POINT_LINK } from '#utils/constants';

@Component({
  template: `{{ 'notifications.politicalExposedPerson.cara' | translate }}`,
  imports: [TranslateModule],
})
export class PEPNotificationComponent {}

@Component({
  template: `@if (daysBeforeExpiration()) {
    {{ 'notifications.companyGrade.date-over' | translate: { daysBeforeExpiration: daysBeforeExpiration() } }}
  }`,
  imports: [NxLinkModule, NxIconModule, TranslateModule],
})
export class CompanyGradeExpirationNotificationComponent {
  readonly daysBeforeExpiration = input<string>();
}

@Component({
  template: `{{ 'notifications.companyGrade.expired' | translate }}
    @if (userBuCode === 'SFAC') {
      <br />
      <nx-link>
        <a href="https://www.allianz-trade.fr/a-propos/implantations/carte.html" target="_blank" class="mt-1">
          <nx-icon name="arrow-right" aria-hidden="true" />
          {{ 'notifications.companyGrade.link.go-to-delegation-list' | translate }}
        </a>
      </nx-link>
    }`,
  imports: [NxLinkModule, NxIconModule, TranslateModule],
})
export class CompanyGradeExpiredNotificationComponent {
  userService: UserService = inject(UserService);
  get userBuCode() {
    return this.userService?.getInternalUser()?.mappedBuCode;
  }
}

@Component({
  template: `{{ 'notifications.companyGrade.missing' | translate }}
    @if (userBuCode === 'SFAC') {
      <br />
      <nx-link>
        <a href="https://www.allianz-trade.fr/a-propos/implantations/carte.html" target="_blank" class="mt-1">
          <nx-icon name="arrow-right" aria-hidden="true" />
          {{ 'notifications.companyGrade.link.go-to-delegation-list' | translate }}
        </a>
      </nx-link>
    }`,
  imports: [NxLinkModule, NxIconModule, TranslateModule],
})
export class CompanyGradeMissingNotificationComponent {
  userService: UserService = inject(UserService);
  get userBuCode() {
    return this.userService?.getInternalUser()?.mappedBuCode;
  }
}

@Component({
  template: `{{ 'notifications.companyGrade.under-review' | translate }}`,
  imports: [TranslateModule],
})
export class CompanyGradeUnderReviewNotificationComponent {}

@Component({
  template: `{{ 'notifications.companyGrade.expired-information' | translate }}`,
  imports: [NxLinkModule, NxIconModule, TranslateModule],
})
export class CompanyGradeInformationNotificationComponent {}

@Component({
  template: `{{ 'notifications.gerl.corp-structure-compliant' | translate }}`,
  imports: [TranslateModule],
})
export class GerlRelatedListedNotificationComponent {}

@Component({
  template: `{{ 'notifications.gerl.additional-check' | translate }}`,
  imports: [TranslateModule],
})
export class GerlUltimateParentUnknownNotificationComponent {}

@Component({
  template: `{{
    'notifications.gerl.unknown-account'
      | translate: { rerunCheckButtonLabel: 'control-detail.button.rerun-check' | translate }
  }}`,
  imports: [TranslateModule],
})
export class GerlAccountNotFoundNotificationComponent {}

@Component({
  template: `{{ 'notifications.energyPolicy.quiz-needed' | translate }}`,
  imports: [TranslateModule],
})
export class EnergyPolicyNotificationComponent {}

@Component({
  template: `{{ 'notifications.sensitiveCountry.quiz-needed' | translate }}`,
  imports: [TranslateModule],
})
export class SensitiveCountryNotificationComponent {}

@Component({
  template: `
    {{ 'notifications.reputationalRisk.risk-definition.part-1' | translate }}
    <br /><br />
    <ul nxList="small">
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.conditions.bullet-1' | translate }}
      </li>
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.conditions.bullet-2' | translate }}
      </li>
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.conditions.bullet-3' | translate }}
      </li>
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.conditions.bullet-4' | translate }}
      </li>
    </ul>
    <br />{{ 'notifications.reputationalRisk.risk-definition.part-2' | translate }}<br /><br />
    {{ 'notifications.reputationalRisk.risk-definition.part-3' | translate }}
    (<nx-link
      ><a href="{{ REP_RISK_SHARE_POINT_LINK }}" target="_blank">{{
        'notifications.reputationalRisk.risk-definition.link-label' | translate
      }}</a></nx-link
    >). <br /><br />
    <ul nxList="small">
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.matrix-score.bullet-1' | translate }}
      </li>
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.matrix-score.bullet-2' | translate }}
      </li>
      <li>
        {{ 'notifications.reputationalRisk.risk-definition.matrix-score.bullet-3' | translate }}
      </li>
    </ul>
  `,
  imports: [NxListModule, NxLinkModule, NxCopytextModule, TranslateModule],
})
export class ReputationalRiskNotificationComponent {
  get REP_RISK_SHARE_POINT_LINK() {
    return REP_RISK_SHARE_POINT_LINK;
  }
}

@Component({
  template: `{{ 'notifications.economicSanctions.attach-documents' | translate }}`,
  imports: [TranslateModule],
})
export class SanctionsSFACFamilyNotificationComponent {}

@Component({
  template: `@if (controlsLabels()) {
    {{ 'notifications.overview.need-rerun' | translate: { controlsLabels: controlsLabels() } }}
  }`,
  imports: [TranslateModule],
})
export class OverviewRerunNotificationComponent {
  readonly controlsLabels = input<string>();
}

@Component({
  template: `
    <div class="flex flex-col">
      {{ 'notifications.overview.todo-controls' | translate }}
      <button
        nxButton="primary small"
        type="button"
        [disabled]="isLoading"
        (click)="submit()"
        class="flex max-w-fit mt-2"
      >
        @if (isLoading) {
          <nx-spinner />
        } @else {
          <nx-icon name="arrow-right" aria-hidden="true" />
          {{ 'notifications.overview.todo-controls.run-todo-controls' | translate }}
        }
      </button>
    </div>
  `,
  imports: [TranslateModule, NxSpinnerModule, NxButtonModule, NxIconModule],
})
export class OverviewRunToDoControlsNotificationComponent {
  readonly controlCodes = input.required<ControllerCode[]>();
  protected readonly cveService: CveService = inject(CveService);
  protected readonly userService: UserService = inject(UserService);
  protected readonly dialogService: NxDialogService = inject(NxDialogService);
  private readonly iterationData = toSignal(this.cveService.iterationData$);
  protected isLoading = false;
  protected async submit() {
    const iterationData = this.iterationData();
    if (!iterationData) return;
    this.isLoading = true;
    try {
      const buCode = iterationData.businessUnitCode ?? this.userService.getInternalUser()?.mappedBuCode;
      if (!buCode) return;
      await lastValueFrom(this.cveService.postIterationCheck(iterationData.iterationId, buCode));
      await Promise.all(
        this.controlCodes().map((code) => this.cveService.pollControllerState(iterationData.iterationId, code)),
      );
    } catch (err) {
      displayError({
        dialogService: this.dialogService,
        err,
      });
    } finally {
      this.isLoading = false;
    }
  }
}

@Component({
  template: `{{ 'notifications.overview.validate' | translate }}`,
  imports: [TranslateModule],
})
export class OverviewValidateNotificationComponent {}

@Component({
  template: `{{ 'notifications.generic.need-rerun' | translate }}`,
  imports: [TranslateModule],
})
export class GenericNeedRerunNotificationComponent {}

@Component({
  template: `{{ 'notifications.generic.pending-too-long' | translate }}`,
  imports: [TranslateModule],
})
export class GenericPendingTooLongNotificationComponent {}

@Component({
  template: `{{ 'notifications.gerl.check-manually' | translate }}
    <nx-link
      ><a href="{{ GERL_SHARE_POINT_LINK }}" target="_blank">{{
        'notifications.gerl.file-link-label' | translate
      }}</a></nx-link
    >`,
  imports: [NxLinkModule, TranslateModule],
})
export class GerlFileComponent {
  get GERL_SHARE_POINT_LINK() {
    return GERL_SHARE_POINT_LINK;
  }
}
